import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import api from '@js/api/authenticatedApi';
import { ADVERT_TYPE_BTR, ADVERT_TYPE_SPARE_ROOM } from '@js/constants';

export const useStudentStore = defineStore('student', () => {
  // Temp variables
  let shortlistFetchPromise = null;
  let shortlistRefreshPromise = null;
  let shortlistedPropertyIdsFetchPromise = null;
  let shortlistedPropertyIdsLastFailedAt = 0;
  let housemateShortlistSettingsPromise = null;

  // state
  const studentHousemateGroups = ref([]);
  const housemateGroupCreateLimit = ref(0);
  const targetHousemateGroup = ref(null);
  const joinHousemateGroup = ref(false);
  const housemateGroupInviteAccepted = ref(false);
  const showHousemateGroupDetail = ref(false);
  const showSearchSubMenu = ref(false);
  const searchSubMenuHeaderText = ref('');
  const searchSubMenuHeaderCount = ref(0);

  const disableGroupCreateButton = ref(false);
  const returnToSearchPage = ref(false);

  const enquiriesProperties = ref([]);
  const enquiriesPropertyCount = ref(0);
  const isHousemateGroupManageMode = ref(false);

  const housemateGroupShortlists = ref([]);
  const housemateGroupShortlistCount = ref(0);
  const housemateGroupShortlistsLoading = ref(false);
  const housemateGroupShortlistsLoadedGroupId = ref(null);

  const hasGroupCreateError = ref(false);
  const hasGroupJoinError = ref(false);
  const groupCreateErrorMsg = ref(null);
  const groupJoinErrorMsg = ref(null);
  const hasGroupManageError = ref(false);
  const groupManageErrorMsg = ref(null);

  const studentId = ref(null);
  const disableFilters = ref(false);

  const housemateGroupShortlistSettingsFetched = ref(false);
  const enableDefaultHousemateShortlist = ref(false);
  const defaultShortlistHousemateId = ref(null);
  const defaultShortlistDestination = ref(null);

  const housemateGroupShortlistedByPropertyId = ref({});
  const housemateGroupPropertyCountsByGroupId = ref({});
  const housemateGroupShortlistedByPropertyIdFetched = ref(false);
  const housemateGroupShortlistedByPropertyIdLoading = ref(false);

  const shortlist = ref([]);
  const sharedShortlist = ref([]);
  const sharedShortlistLoading = ref(false);
  const shortlistFetched = ref(false);
  const shortlistLoadedOnce = ref(false);
  const shortlistCount = ref(0);
  const selectedPropertyIds = ref([]);

  const shortlistedPropertyIds = ref([]);
  const shortlistedPropertyIdsFetched = ref(false);

  const defaultHousemateGroupId = ref(null);

  const isRefreshingManageGroup = ref(false);

  const toNumber = (value) => {
    const num = typeof value === 'string' ? Number(value) : value;
    return Number.isFinite(num) ? num : null;
  };

  const normaliseId = (value) => String(value ?? '');
  const idsMatch = (left, right) => normaliseId(left) === normaliseId(right);

  const toUiHousemateGroupId = (groupId) => {
    const asString = String(groupId ?? '').trim();
    if (!asString) {
      return null;
    }

    // Already using API/UI obfuscated id.
    if (
      studentHousemateGroups.value.some(
        (group) => String(group?.housemate_group_id ?? '') === asString
      )
    ) {
      return asString;
    }

    // Fall back from raw DB id if present in nested relation payloads.
    const matchedGroup = studentHousemateGroups.value.find(
      (group) => String(group?.housemate_group?.id ?? '') === asString
    );

    return matchedGroup?.housemate_group_id
      ? String(matchedGroup.housemate_group_id)
      : asString;
  };

  const normalisePropertiesPayload = (propertiesPayload) => {
    if (Array.isArray(propertiesPayload)) {
      return propertiesPayload;
    }

    if (Array.isArray(propertiesPayload?.data)) {
      return propertiesPayload.data;
    }

    return [];
  };

  const getCountFromApiOrArrayLength = (countCandidate, arrayCandidate) => {
    const asNumber = Number(countCandidate);
    if (Number.isFinite(asNumber)) {
      return asNumber;
    }

    return Array.isArray(arrayCandidate) ? arrayCandidate.length : 0;
  };

  const isShortlistHeaderActive = () => {
    return (
      showSearchSubMenu.value &&
      searchSubMenuHeaderText.value === 'My shortlist'
    );
  };

  const syncShortlistHeaderCount = () => {
    if (isShortlistHeaderActive()) {
      searchSubMenuHeaderCount.value = shortlistCount.value;
    }
  };

  const getPropertyBedrooms = (property) => {
    const candidates = [
      property?.bedroomCount,
      property?.bedrooms,
      property?.roomCount,
      property?.bedroom_count,
    ];

    for (const candidate of candidates) {
      const num = toNumber(candidate);
      if (num !== null) {
        return num;
      }
    }

    return null;
  };

  const getPropertyBathrooms = (property) => {
    const candidates = [
      property?.bathroomCount,
      property?.bathrooms,
      property?.bathroom_count,
    ];

    for (const candidate of candidates) {
      const num = toNumber(candidate);
      if (num !== null) {
        return num;
      }
    }

    return null;
  };

  const getGroupVoteYesCount = (property) => {
    const candidates = [
      property?.groupVoteYesCount,
      property?.group_vote_yes_count,
    ];

    for (const value of candidates) {
      const num = Number(value);
      if (Number.isFinite(num)) {
        return num;
      }
    }

    return 0;
  };

  const getGroupVoteNoCount = (property) => {
    const candidates = [
      property?.groupVoteNoCount,
      property?.group_vote_no_count,
    ];

    for (const value of candidates) {
      const num = Number(value);
      if (Number.isFinite(num)) {
        return num;
      }
    }

    return 0;
  };

  const hasPropertyBeenEnquired = (property) => {
    return Boolean(
      property?.lastEnquiredAt ??
        property?.last_enquired_at ??
        property?.housemateGroupLastEnquiredAt ??
        property?.housemate_group_last_enquired_at ??
        false
    );
  };

  const shouldOpenHousemateGroupManage = (group = null) => {
    const targetGroup = group || targetHousemateGroup.value;

    if (!targetGroup || targetGroup.status !== 'APPROVED') {
      return false;
    }

    const members = Array.isArray(targetGroup?.members)
      ? targetGroup.members
      : Array.isArray(targetGroup?.housemate_group_members)
      ? targetGroup.housemate_group_members
      : [];

    if (!members.length) {
      return false;
    }

    const pendingCount = members.filter(
      (member) => member?.status === 'PENDING'
    ).length;
    const approvedCount = members.filter(
      (member) => member?.status === 'APPROVED'
    ).length;

    return pendingCount > 0 || approvedCount === 1;
  };

  const sortHousemateGroupPropertiesByVotes = (properties = []) => {
    if (!Array.isArray(properties)) {
      return [];
    }

    return [...properties].sort((a, b) => {
      const aEnquired = hasPropertyBeenEnquired(a);
      const bEnquired = hasPropertyBeenEnquired(b);

      if (aEnquired !== bEnquired) {
        return aEnquired ? 1 : -1;
      }

      const aYes = getGroupVoteYesCount(a);
      const bYes = getGroupVoteYesCount(b);

      if (aYes !== bYes) {
        return bYes - aYes;
      }

      const aNo = getGroupVoteNoCount(a);
      const bNo = getGroupVoteNoCount(b);

      if (aNo !== bNo) {
        return aNo - bNo;
      }

      const aId = String(a?.id ?? '');
      const bId = String(b?.id ?? '');
      return aId.localeCompare(bId);
    });
  };

  const getPropertyCheapestPrice = (property) => {
    const candidates = [
      property?.cheapestRoomPrice,
      property?.price,
      property?.cheapest_room_price,
    ];

    for (const candidate of candidates) {
      const num = toNumber(candidate);
      if (num !== null) {
        return num;
      }
    }

    return null;
  };

  const propertyMatchesFilters = (property, filters = {}) => {
    if (!property) {
      return false;
    }

    const useExactRoomCounts = Boolean(filters.useExactRoomCounts);

    const targetBedrooms = toNumber(filters.bedrooms);
    if (targetBedrooms !== null) {
      const propertyBedrooms = getPropertyBedrooms(property);
      if (propertyBedrooms !== null) {
        if (useExactRoomCounts) {
          if (propertyBedrooms !== targetBedrooms) {
            return false;
          }
        } else {
          if (propertyBedrooms < targetBedrooms) {
            return false;
          }
        }
      }
    }

    const targetBathrooms = toNumber(filters.bathrooms);
    if (targetBathrooms !== null) {
      const propertyBathrooms = getPropertyBathrooms(property);
      if (propertyBathrooms !== null) {
        if (useExactRoomCounts) {
          if (propertyBathrooms !== targetBathrooms) {
            return false;
          }
        } else {
          if (propertyBathrooms < targetBathrooms) {
            return false;
          }
        }
      }
    }

    const propertyPrice = getPropertyCheapestPrice(property);

    if (filters.minPriceSelected) {
      const minPrice = toNumber(filters.minPrice);
      if (
        minPrice !== null &&
        propertyPrice !== null &&
        propertyPrice < minPrice
      ) {
        return false;
      }
    }

    if (filters.maxPriceSelected) {
      const maxPrice = toNumber(filters.maxPrice);
      if (
        maxPrice !== null &&
        propertyPrice !== null &&
        propertyPrice > maxPrice
      ) {
        return false;
      }
    }

    const typeFilter = filters.type;
    if (typeFilter) {
      const selectedTypes = Array.isArray(typeFilter)
        ? typeFilter
        : String(typeFilter).split(',').filter(Boolean);

      if (selectedTypes.length) {
        const advertType = property?.advertType;
        const matchesSpareRooms =
          selectedTypes.includes('spare-rooms') &&
          advertType === ADVERT_TYPE_SPARE_ROOM;
        const matchesPrivateHalls =
          selectedTypes.includes('private-halls') &&
          advertType === ADVERT_TYPE_BTR;
        const matchesStructuralType =
          advertType !== ADVERT_TYPE_SPARE_ROOM &&
          advertType !== ADVERT_TYPE_BTR &&
          selectedTypes.includes(property?.type);

        if (
          !matchesSpareRooms &&
          !matchesPrivateHalls &&
          !matchesStructuralType
        ) {
          return false;
        }
      }
    }

    const landlordId = filters.lettingAgent;
    if (landlordId && property?.landlord?.obfuscatedId) {
      if (String(property.landlord.obfuscatedId) !== String(landlordId)) {
        return false;
      }
    }

    return true;
  };
  const setShortlistCount = (count) => {
    const asNumber = Number(count);
    if (Number.isFinite(asNumber) && asNumber >= 0) {
      shortlistCount.value = asNumber;
      syncShortlistHeaderCount();
    }
  };

  const hydrateShortlist = (properties = [], count = null) => {
    shortlist.value = Array.isArray(properties) ? [...properties] : [];
    shortlistLoadedOnce.value = true;
    setShortlistCount(count ?? shortlist.value.length);
    shortlistedPropertyIds.value = shortlist.value.map(
      (property) => property.id
    );
    shortlistedPropertyIdsFetched.value = true;
    syncSelectionWithShortlist();
  };

  const hydrateSharedShortlist = (properties = []) => {
    sharedShortlist.value = Array.isArray(properties) ? [...properties] : [];
  };

  // actions

  /** API Calls **/

  const getEnquiredProperties = async ({
    page = 1,
    bedrooms,
    bathrooms,
    minPrice,
    maxPrice,
    type,
    sort,
    view,
    lettingAgent,
  } = {}) => {
    const params = {
      'enquired-properties': 1,
      reload: 'true',
    };

    if (page && Number(page) > 1) {
      params.page = Number(page);
    }

    if (bedrooms) {
      params.bedrooms = bedrooms;
    }
    if (bathrooms) {
      params.bathrooms = bathrooms;
    }
    if (minPrice) {
      params['min-price'] = minPrice;
    }
    if (maxPrice) {
      params['max-price'] = maxPrice;
    }
    if (type) {
      params.type = type;
    }
    if (sort) {
      params.sort = sort;
    }
    if (view) {
      params.view = view;
    }
    if (lettingAgent) {
      params['letting-agent'] = lettingAgent;
    }

    const response = await api.get('/student-accommodation', { params });
    const data = response?.data ?? {};

    enquiriesProperties.value = Array.isArray(data.properties)
      ? data.properties
      : [];
    enquiriesPropertyCount.value =
      typeof data.propertyCount === 'number'
        ? data.propertyCount
        : enquiriesProperties.value.length;

    return data;
  };

  const getHousemateGroups = async () => {
    try {
      const response = await api.get('/account/housemate-group/members');
      studentHousemateGroups.value = response.data;

      if (studentHousemateGroups.value.length > 0) {
        disableFilters.value = false;
      }

      return response.data;
    } catch (error) {
      return null;
    }
  };

  const getHousemateGroupShortlists = async (
    housemateGroupId = null,
    { showLoading = true } = {}
  ) => {
    if (showLoading) {
      housemateGroupShortlistsLoading.value = true;
    }
    try {
      const response = await api.get(
        `/account/housemate-group/shortlists/${housemateGroupId}/properties/full`
      );

      const properties = normalisePropertiesPayload(response.data?.properties);

      housemateGroupShortlists.value = [...properties];

      housemateGroupShortlistCount.value = getCountFromApiOrArrayLength(
        response.data?.propertyCount,
        properties
      );

      housemateGroupShortlistsLoadedGroupId.value = housemateGroupId;

      return properties;
    } catch (error) {
      // TODO add error message to UI
      return null;
    } finally {
      if (showLoading) {
        housemateGroupShortlistsLoading.value = false;
      }
    }
  };

  const voteHousemateGroupShortlistProperty = async ({
    housemateGroupId,
    propertyId,
    vote,
  } = {}) => {
    if (!housemateGroupId || !propertyId || !['yes', 'no'].includes(vote)) {
      return null;
    }

    try {
      const response = await api.post(
        `/account/housemate-group/shortlists/${housemateGroupId}/properties/${propertyId}/vote`,
        { vote }
      );

      const payload = response?.data ?? null;

      if (!payload) {
        return null;
      }

      const targetPropertyIds = new Set([
        String(propertyId),
        String(payload.property_id ?? ''),
      ]);

      housemateGroupShortlists.value = housemateGroupShortlists.value.map(
        (property) => {
          const candidateIds = [
            String(property?.id ?? ''),
            String(property?.propertyId ?? ''),
            String(property?.property_id ?? ''),
          ];

          const isTargetProperty = candidateIds.some((candidateId) =>
            targetPropertyIds.has(candidateId)
          );

          if (!isTargetProperty) {
            return property;
          }

          return {
            ...property,
            groupVote:
              typeof payload.vote === 'string'
                ? payload.vote.toLowerCase()
                : null,
            groupVoteYesCount: Number(payload.group_vote_yes_count ?? 0),
            groupVoteNoCount: Number(payload.group_vote_no_count ?? 0),
            groupVoteHasNo: Boolean(payload.group_vote_has_no ?? false),
          };
        }
      );

      return payload;
    } catch (error) {
      return error?.response?.data ?? null;
    }
  };

  const postHousemateGroup = async (
    inputHousemateGroupName,
    cityId = null,
    universityId = null
  ) => {
    hasGroupCreateError.value = false;
    groupCreateErrorMsg.value = null;

    const existingGroupIds = new Set(
      (studentHousemateGroups.value || []).map((group) =>
        String(group?.housemate_group_id ?? '')
      )
    );

    let createdGroupId = null;

    try {
      const payload = {
        name: inputHousemateGroupName,
      };

      const normalizedCityId = Number.parseInt(cityId, 10);
      const normalizedUniversityId = Number.parseInt(universityId, 10);

      if (Number.isInteger(normalizedCityId) && normalizedCityId > 0) {
        payload.city_id = normalizedCityId;
      }

      if (
        Number.isInteger(normalizedUniversityId) &&
        normalizedUniversityId > 0
      ) {
        payload.university_id = normalizedUniversityId;
      }

      const response = await api.post(
        '/account/housemate-group/members',
        payload
      );

      createdGroupId = response?.data?.housemate_group_id
        ? String(response.data.housemate_group_id)
        : null;
    } catch (error) {
      hasGroupCreateError.value = true;

      if (error.response?.data?.errors || error.response?.data?.error) {
        // We've got a validation error response from the server
        groupCreateErrorMsg.value = normalizeServerErrors(
          error.response?.data?.errors || error.response?.data?.message
        );
      }
    }

    await getHousemateGroups();

    if (!createdGroupId) {
      const newGroup = (studentHousemateGroups.value || []).find((group) => {
        const id = String(group?.housemate_group_id ?? '');
        return id && !existingGroupIds.has(id) && group?.status === 'APPROVED';
      });

      createdGroupId = newGroup?.housemate_group_id
        ? String(newGroup.housemate_group_id)
        : null;
    }

    if (!hasGroupCreateError.value && createdGroupId) {
      await postSetDefaultHousemateGroup(createdGroupId);
      return createdGroupId;
    }

    setTargetHousemateGroup();
    return null;
  };

  const postSetDefaultHousemateGroup = async (groupId) => {
    // Set the selected housemate group
    setTargetHousemateGroup(groupId);

    // Keep the grouplist property count in sync when changing groups.
    if (showSearchSubMenu.value || showHousemateGroupDetail.value) {
      await getHousemateGroupShortlists(groupId);
      searchSubMenuHeaderCount.value = Number.isFinite(
        housemateGroupShortlistCount.value
      )
        ? housemateGroupShortlistCount.value
        : 0;
    }

    // Persist in the database
    try {
      await api.post('/account/housemate-group/default-group', {
        housemate_group_id: groupId,
      });

      // Get all the users groups
      await getHousemateGroups();
    } catch (error) {
      // TODO add error message to UI
      return null;
    }
  };

  const getHousemateGroupShortlistSettings = async () => {
    if (housemateGroupShortlistSettingsFetched.value) {
      return {
        enable_default_housemate_shortlist:
          enableDefaultHousemateShortlist.value,
        default_shortlist_housemate_id: defaultShortlistHousemateId.value,
        default_shortlist_destination: defaultShortlistDestination.value,
      };
    }

    if (housemateShortlistSettingsPromise) {
      return housemateShortlistSettingsPromise;
    }

    housemateShortlistSettingsPromise = (async () => {
      try {
        const response = await api.get(
          '/account/housemate-group/shortlist-settings'
        );

        enableDefaultHousemateShortlist.value =
          !!response?.data?.enable_default_housemate_shortlist;
        defaultShortlistHousemateId.value =
          response?.data?.default_shortlist_housemate_id ?? null;
        defaultShortlistDestination.value =
          response?.data?.default_shortlist_destination ??
          (enableDefaultHousemateShortlist.value
            ? defaultShortlistHousemateId.value
              ? 'group'
              : 'personal'
            : null);
        housemateGroupShortlistSettingsFetched.value = true;

        return response.data;
      } catch (err) {
        // If routes are cached and this endpoint hasn't been deployed yet,
        // treat as "no default" rather than throwing and breaking UX.
        enableDefaultHousemateShortlist.value = false;
        defaultShortlistHousemateId.value = null;
        defaultShortlistDestination.value = null;
        housemateGroupShortlistSettingsFetched.value = true;

        return {
          enable_default_housemate_shortlist: false,
          default_shortlist_housemate_id: null,
          default_shortlist_destination: null,
        };
      } finally {
        housemateShortlistSettingsPromise = null;
      }
    })();

    return housemateShortlistSettingsPromise;
  };

  const getShortlist = async (
    params = {},
    force = false,
    { preserveOnError = false } = {}
  ) => {
    if (shortlistFetched.value && !force) {
      return shortlist.value;
    }

    if (shortlistFetchPromise && !force) {
      return shortlistFetchPromise;
    }

    shortlistFetchPromise = (async () => {
      try {
        let response;

        try {
          response = await api.get('/student/shortlist/properties/full', {
            params,
          });
        } catch {
          // Staging can occasionally race after auth deploys/cookie refresh.
          // Retry once before giving up to avoid showing a stale persisted count.
          response = await api.get('/student/shortlist/properties/full', {
            params,
          });
        }

        shortlist.value = response.data.shortlisted;
        shortlistFetched.value = true;
        shortlistLoadedOnce.value = true;

        const countFromApi = response.data?.count;
        setShortlistCount(
          getCountFromApiOrArrayLength(countFromApi, shortlist.value)
        );

        syncSelectionWithShortlist();
        syncShortlistHeaderCount();

        shortlistedPropertyIds.value = Array.isArray(shortlist.value)
          ? shortlist.value.map((property) => property.id)
          : [];
        shortlistedPropertyIdsFetched.value = true;

        return shortlist.value;
      } catch (error) {
        if (preserveOnError) throw error;

        shortlist.value = [];
        shortlistFetched.value = false;
        shortlistLoadedOnce.value = true;
        setShortlistCount(0);
        selectedPropertyIds.value = [];
        shortlistedPropertyIds.value = [];
        shortlistedPropertyIdsFetched.value = false;
        syncShortlistHeaderCount();

        return shortlist.value;
      } finally {
        shortlistFetchPromise = null;
      }
    })();

    return shortlistFetchPromise;
  };

  const getSharedShortlist = async (shortlistHash, params = {}) => {
    if (!shortlistHash) {
      sharedShortlist.value = [];
      return sharedShortlist.value;
    }

    sharedShortlistLoading.value = true;

    try {
      const response = await api.get(
        `/student/shortlist/shared/${encodeURIComponent(
          shortlistHash
        )}/properties/full`,
        { params }
      );
      sharedShortlist.value = Array.isArray(response?.data?.shortlisted)
        ? response.data.shortlisted
        : [];

      return sharedShortlist.value;
    } finally {
      sharedShortlistLoading.value = false;
    }
  };

  const filterSharedShortlist = (filters = {}) => {
    const list = Array.isArray(sharedShortlist.value)
      ? sharedShortlist.value
      : [];
    return list.filter((property) => propertyMatchesFilters(property, filters));
  };

  const createShortlistShareLink = async () => {
    const response = await api.post('/student/shortlist/share');

    return typeof response?.data === 'string' ? response.data : null;
  };

  const refreshShortlist = () => {
    if (shortlistRefreshPromise) return shortlistRefreshPromise;

    shortlistRefreshPromise = (async () => {
      try {
        // Finish requests frozen during navigation before fetching current state.
        await Promise.allSettled([
          shortlistFetchPromise,
          shortlistedPropertyIdsFetchPromise,
        ]);
        invalidateShortlist();
        return await getShortlist({}, true, { preserveOnError: true });
      } finally {
        shortlistRefreshPromise = null;
      }
    })();

    return shortlistRefreshPromise;
  };

  const postUpdateHousemateGroupShortlistSettings = async ({
    enableDefault = false,
    defaultHousemateGroupId = null,
    destination = null,
  } = {}) => {
    const normalizedDestination = enableDefault
      ? destination ?? (defaultHousemateGroupId ? 'group' : 'personal')
      : null;

    const payload = {
      enable_default_housemate_shortlist: !!enableDefault,
      default_shortlist_destination: normalizedDestination,
      default_shortlist_housemate_id:
        enableDefault && normalizedDestination === 'group'
          ? defaultHousemateGroupId
          : null,
    };

    const response = await api.post(
      '/account/housemate-group/shortlist-settings',
      payload
    );

    enableDefaultHousemateShortlist.value =
      !!response?.data?.enable_default_housemate_shortlist;
    defaultShortlistHousemateId.value =
      response?.data?.default_shortlist_housemate_id ?? null;
    defaultShortlistDestination.value =
      response?.data?.default_shortlist_destination ??
      (enableDefaultHousemateShortlist.value
        ? defaultShortlistHousemateId.value
          ? 'group'
          : 'personal'
        : null);
    housemateGroupShortlistSettingsFetched.value = true;

    return response.data;
  };

  const getShortlistedPropertyIds = async () => {
    if (shortlistedPropertyIdsFetched.value) {
      return shortlistedPropertyIds.value;
    }

    if (shortlistedPropertyIdsFetchPromise) {
      return shortlistedPropertyIdsFetchPromise;
    }

    // Avoid repeated request storms when the endpoint is currently failing.
    const now = Date.now();
    if (now - shortlistedPropertyIdsLastFailedAt < 5000) {
      return shortlistedPropertyIds.value;
    }

    shortlistedPropertyIdsFetchPromise = (async () => {
      try {
        const response = await api.get('/student/shortlist/properties');
        const ids = Array.isArray(response?.data?.shortlisted)
          ? response.data.shortlisted
          : [];

        shortlistedPropertyIds.value = ids.map((id) => normaliseId(id));
        shortlistedPropertyIdsFetched.value = true;

        return ids;
      } catch (error) {
        shortlistedPropertyIdsLastFailedAt = Date.now();
        throw error;
      } finally {
        shortlistedPropertyIdsFetchPromise = null;
      }
    })();

    return shortlistedPropertyIdsFetchPromise;
  };

  const removeFromShortlist = async (propertyId) => {
    await api.post(`/student/shortlist/remove/${propertyId}`);
    invalidateShortlist();
    shortlistedPropertyIdsFetched.value = false;

    const currentShortlistedIds = Array.isArray(shortlistedPropertyIds.value)
      ? shortlistedPropertyIds.value
      : [];
    shortlistedPropertyIds.value = currentShortlistedIds.filter(
      (id) => !idsMatch(id, propertyId)
    );

    shortlist.value = shortlist.value.filter(
      (property) => !idsMatch(property?.id, propertyId)
    );

    setShortlistCount(shortlist.value.length);

    deselectShortlistProperty(propertyId);
    syncShortlistHeaderCount();

    // Reconcile against authoritative server state.
    await getShortlist();
  };

  const postJoinHousemateGroup = async (groupId) => {
    hasGroupJoinError.value = false;
    groupJoinErrorMsg.value = null;

    return await api
      .post(`/account/housemate-group/${groupId}/join`)
      .then((response) => {
        // housemateGroups.value = response.data;
        return response.data;
      })
      .catch((error) => {
        setHousemateGroupJoinError(error.response?.data?.message);
        return null;
      });
  };

  const postAddToShortlist = async (propertyId, property = null) => {
    if (!propertyId) {
      return null;
    }

    const beforeList = Array.isArray(shortlist.value)
      ? [...shortlist.value]
      : [];
    const beforeCount = shortlistCount.value;

    const didInsertOptimistically = Boolean(property?.id);
    if (didInsertOptimistically) {
      shortlist.value = insertPropertyIntoList(shortlist.value, property);
      if (shortlist.value.length !== beforeList.length) {
        setShortlistCount(shortlist.value.length);
      }
      syncShortlistHeaderCount();
    }

    try {
      const response = await api.post(`/student/shortlist/add/${propertyId}`);
      const { success, count } = response?.data ?? {};

      if (success) {
        setShortlistCount(count);
        invalidateShortlist();

        const ids = Array.isArray(shortlistedPropertyIds.value)
          ? shortlistedPropertyIds.value
          : [];
        if (!ids.some((id) => idsMatch(id, propertyId))) {
          shortlistedPropertyIds.value = [...ids, normaliseId(propertyId)];
        }
        shortlistedPropertyIdsFetched.value = true;
        syncShortlistHeaderCount();

        return response;
      }

      if (didInsertOptimistically) {
        shortlist.value = beforeList;
        setShortlistCount(beforeCount);
        syncShortlistHeaderCount();
      }

      return response;
    } catch (error) {
      if (didInsertOptimistically) {
        shortlist.value = beforeList;
        setShortlistCount(beforeCount);
        syncShortlistHeaderCount();
      }

      throw error;
    }
  };

  const postRenameHousemateGroup = async ({ housemateGroupId, name } = {}) => {
    clearManageError();

    if (!housemateGroupId) {
      setHousemateGroupManageError('Missing group id.');
      return null;
    }

    try {
      const response = await api.patch(
        `/account/housemate-group/${housemateGroupId}`,
        {
          name,
        }
      );

      await refreshHousemateGroupsAndTarget(housemateGroupId);

      return response.data;
    } catch (error) {
      setHousemateGroupManageError(error.response?.data?.message);
      return null;
    }
  };

  const deleteHousemateGroup = async ({ housemateGroupId } = {}) => {
    clearManageError();

    if (!housemateGroupId) {
      setHousemateGroupManageError('Missing group id.');
      return false;
    }

    try {
      await api.delete(`/account/housemate-group/${housemateGroupId}`);
      await refreshHousemateGroupsAndTarget();

      return true;
    } catch (error) {
      setHousemateGroupManageError(error.response?.data?.message);
      return false;
    }
  };

  const approveHousemateGroupMember = async ({
    housemateGroupId,
    memberStudentId,
  } = {}) => {
    clearManageError();

    if (!housemateGroupId || !memberStudentId) {
      setHousemateGroupManageError('Missing member id.');
      return null;
    }

    try {
      const response = await api.patch(
        `/account/housemate-group/${housemateGroupId}/members/${memberStudentId}/approve`
      );

      await refreshHousemateGroupsAndTarget(housemateGroupId);

      return response.data;
    } catch (error) {
      setHousemateGroupManageError(error.response?.data?.message);
      return null;
    }
  };

  const declineHousemateGroupMember = async ({
    housemateGroupId,
    memberStudentId,
  } = {}) => {
    clearManageError();

    if (!housemateGroupId || !memberStudentId) {
      setHousemateGroupManageError('Missing member id.');
      return null;
    }

    try {
      const response = await api.patch(
        `/account/housemate-group/${housemateGroupId}/members/${memberStudentId}/decline`
      );

      await refreshHousemateGroupsAndTarget(housemateGroupId);

      return response.data;
    } catch (error) {
      setHousemateGroupManageError(error.response?.data?.message);
      return null;
    }
  };

  const leaveHousemateGroup = async ({ housemateGroupId } = {}) => {
    clearManageError();

    if (!housemateGroupId) {
      setHousemateGroupManageError('Missing group id.');
      return false;
    }

    try {
      await api.delete(`/account/housemate-group/${housemateGroupId}/leave`);

      await getHousemateGroups();
      setTargetHousemateGroup();

      return true;
    } catch (error) {
      setHousemateGroupManageError(error.response?.data?.message);
      return false;
    }
  };

  const removeHousemateGroupMember = async ({
    housemateGroupId,
    memberStudentId,
  } = {}) => {
    clearManageError();

    if (!housemateGroupId || !memberStudentId) {
      setHousemateGroupManageError('Missing member id.');
      return null;
    }

    try {
      const response = await api.delete(
        `/account/housemate-group/${housemateGroupId}/members/${memberStudentId}`
      );

      await refreshHousemateGroupsAndTarget(housemateGroupId);

      return response.data;
    } catch (error) {
      setHousemateGroupManageError(error.response?.data?.message);
      return null;
    }
  };

  const getHousemateGroupShortlistedPropertyIds = async ({
    force = false,
  } = {}) => {
    if (!force && housemateGroupShortlistedByPropertyIdFetched.value) {
      return housemateGroupShortlistedByPropertyId.value;
    }

    housemateGroupShortlistedByPropertyIdLoading.value = true;

    try {
      const response = await api.get(
        '/account/housemate-group/shortlists/property-ids'
      );
      const map = response?.data?.property_to_housemate_group;
      const groupCounts = response?.data?.group_property_counts;

      housemateGroupShortlistedByPropertyId.value =
        map && typeof map === 'object' ? map : {};
      housemateGroupPropertyCountsByGroupId.value =
        groupCounts && typeof groupCounts === 'object' ? groupCounts : {};
      housemateGroupShortlistedByPropertyIdFetched.value = true;

      return housemateGroupShortlistedByPropertyId.value;
    } finally {
      housemateGroupShortlistedByPropertyIdLoading.value = false;
    }
  };

  const removeFromHousemateGroupShortlist = async ({
    housemateGroupId,
    propertyId,
    refreshGroup = true,
  } = {}) => {
    if (!housemateGroupId || !propertyId) {
      return null;
    }

    const resolvedGroupId = String(housemateGroupId || '');
    const activeGroupId = targetHousemateGroup.value?.housemate_group_id;
    const isViewingThisGroupDetail =
      showHousemateGroupDetail.value &&
      String(activeGroupId || '') === resolvedGroupId;

    const beforeList = Array.isArray(housemateGroupShortlists.value)
      ? [...housemateGroupShortlists.value]
      : [];
    const beforeCount = housemateGroupShortlistCount.value;

    const didRemoveOptimistically = isViewingThisGroupDetail;
    if (didRemoveOptimistically) {
      housemateGroupShortlists.value = removePropertyFromList(
        housemateGroupShortlists.value,
        propertyId
      );
      housemateGroupShortlistCount.value = Array.isArray(
        housemateGroupShortlists.value
      )
        ? housemateGroupShortlists.value.length
        : 0;
      searchSubMenuHeaderCount.value = housemateGroupShortlistCount.value;
    }

    let response;
    try {
      response = await api.delete(
        `/account/housemate-group/shortlists/${housemateGroupId}/${propertyId}`
      );
    } catch (error) {
      if (didRemoveOptimistically) {
        housemateGroupShortlists.value = beforeList;
        housemateGroupShortlistCount.value = beforeCount;
        searchSubMenuHeaderCount.value = Number.isFinite(beforeCount)
          ? beforeCount
          : 0;
      }
      throw error;
    }

    // Ensure hearts can update immediately after remove.
    const current = {
      ...(housemateGroupShortlistedByPropertyId.value || {}),
    };
    delete current[String(propertyId)];
    housemateGroupShortlistedByPropertyId.value = current;
    housemateGroupShortlistedByPropertyIdFetched.value = true;

    // Same stale-list protection as add: if we removed from a group while not
    // actively viewing that group's detail, invalidate the cached loaded id so
    // the next tab open re-fetches.
    if (
      !isViewingThisGroupDetail &&
      String(housemateGroupShortlistsLoadedGroupId.value || '') ===
        resolvedGroupId
    ) {
      housemateGroupShortlistsLoadedGroupId.value = null;
    }

    if (refreshGroup) {
      await syncHousemateGroupShortlistsIfActive(housemateGroupId);
    }

    return response.data;
  };

  const removeMultipleFromShortlist = async (propertyIds) => {
    if (!propertyIds.length) {
      return;
    }

    const response = await api.post('/student/shortlist/remove-many', {
      property_ids: propertyIds.map((id) => id.toString()),
    });

    if (response.data.success) {
      const idsToRemove = new Set(
        propertyIds.map((id) => normaliseId(id)).filter(Boolean)
      );

      const currentShortlistedIds = Array.isArray(shortlistedPropertyIds.value)
        ? shortlistedPropertyIds.value
        : [];
      shortlistedPropertyIds.value = currentShortlistedIds.filter(
        (id) => !idsToRemove.has(normaliseId(id))
      );

      shortlist.value = shortlist.value.filter(
        (property) => !idsToRemove.has(normaliseId(property?.id))
      );

      shortlistedPropertyIdsFetched.value = false;

      setShortlistCount(shortlist.value.length);

      selectedPropertyIds.value = selectedPropertyIds.value.filter(
        (id) => !idsToRemove.has(normaliseId(id))
      );

      syncShortlistHeaderCount();

      // Reconcile against authoritative server state.
      invalidateShortlist();
      await getShortlist();
    }
  };

  const addToHousemateGroupShortlist = async ({
    housemateGroupId,
    propertyId,
    property = null,
    setAsDefault = false,
    refreshGroup = true,
  } = {}) => {
    if (!housemateGroupId || !propertyId) {
      return null;
    }

    const resolvedGroupId = String(housemateGroupId || '');
    const activeGroupId = targetHousemateGroup.value?.housemate_group_id;
    const isViewingThisGroupDetail =
      showHousemateGroupDetail.value &&
      String(activeGroupId || '') === resolvedGroupId;

    const beforeList = Array.isArray(housemateGroupShortlists.value)
      ? [...housemateGroupShortlists.value]
      : [];
    const beforeCount = housemateGroupShortlistCount.value;

    const didInsertOptimistically =
      Boolean(property?.id) && isViewingThisGroupDetail;
    if (didInsertOptimistically) {
      housemateGroupShortlists.value = insertPropertyIntoList(
        housemateGroupShortlists.value,
        property
      );
      housemateGroupShortlistCount.value = Array.isArray(
        housemateGroupShortlists.value
      )
        ? housemateGroupShortlists.value.length
        : 0;
      searchSubMenuHeaderCount.value = housemateGroupShortlistCount.value;
    }

    let response;
    try {
      response = await api.post(
        `/account/housemate-group/shortlists/${housemateGroupId}`,
        {
          property_id: propertyId,
          housemate_group_id: housemateGroupId,
          default_housemate_group_shortlist_setting: !!setAsDefault,
        }
      );
    } catch (error) {
      if (didInsertOptimistically) {
        housemateGroupShortlists.value = beforeList;
        housemateGroupShortlistCount.value = beforeCount;
        searchSubMenuHeaderCount.value = Number.isFinite(beforeCount)
          ? beforeCount
          : 0;
      }

      throw error;
    }

    if (setAsDefault) {
      enableDefaultHousemateShortlist.value = true;
      defaultShortlistHousemateId.value = housemateGroupId;
      defaultShortlistDestination.value = 'group';
      housemateGroupShortlistSettingsFetched.value = true;
    }

    // Ensure hearts can render immediately after add.
    housemateGroupShortlistedByPropertyId.value = {
      ...(housemateGroupShortlistedByPropertyId.value || {}),
      [String(propertyId)]: String(housemateGroupId),
    };
    housemateGroupShortlistedByPropertyIdFetched.value = true;

    // If the user isn't currently viewing this grouplist detail UI, ensure
    // we don't consider any previously loaded grouplist properties list as
    // authoritative. This forces a refresh next time the grouplist tab opens.
    if (
      !isViewingThisGroupDetail &&
      String(housemateGroupShortlistsLoadedGroupId.value || '') ===
        resolvedGroupId
    ) {
      housemateGroupShortlistsLoadedGroupId.value = null;
    }

    // If we optimistically inserted into the active list, keep count aligned.
    if (didInsertOptimistically) {
      housemateGroupShortlistCount.value = Array.isArray(
        housemateGroupShortlists.value
      )
        ? housemateGroupShortlists.value.length
        : 0;
      searchSubMenuHeaderCount.value = housemateGroupShortlistCount.value;
    }

    if (refreshGroup) {
      await syncHousemateGroupShortlistsIfActive(housemateGroupId);
    }

    return response.data;
  };

  /** MISC Helpers **/

  const setTargetHousemateGroup = (
    groupId = null,
    { updateMenuHeader = true } = {}
  ) => {
    const currentGrouplistPropertiesCount = Number.isFinite(
      housemateGroupShortlistCount.value
    )
      ? housemateGroupShortlistCount.value
      : 0;

    // A specific group id is provided
    if (groupId) {
      const housemateGroup = studentHousemateGroups.value?.find((group) => {
        return (
          String(group.housemate_group_id) === String(groupId) &&
          group.status !== 'PENDING' &&
          group.status !== 'REJECTED'
        );
      });

      if (housemateGroup) {
        targetHousemateGroup.value = housemateGroup;
        if (updateMenuHeader) {
          setMenuHeaderText(
            targetHousemateGroup.value?.housemate_group_name,
            currentGrouplistPropertiesCount
          );
        }
        return;
      }
    }

    // Keep the current target if it is still an approved group.
    const currentTarget = targetHousemateGroup.value;
    const currentTargetId = currentTarget?.housemate_group_id;
    const existingCurrentTarget = studentHousemateGroups.value?.find(
      (studentHousemateGroup) => {
        return (
          String(studentHousemateGroup?.housemate_group_id ?? '') ===
            String(currentTargetId ?? '') &&
          studentHousemateGroup.status !== 'PENDING' &&
          studentHousemateGroup.status !== 'REJECTED'
        );
      }
    );

    if (existingCurrentTarget) {
      targetHousemateGroup.value = existingCurrentTarget;
      if (updateMenuHeader) {
        setMenuHeaderText(
          targetHousemateGroup.value?.housemate_group_name,
          currentGrouplistPropertiesCount
        );
      }
      return;
    }

    // Filter data to set default group
    const persistedDefault = studentHousemateGroups.value?.find(
      (studentHousemateGroup) => {
        return (
          studentHousemateGroup.housemate_group_id ===
            defaultHousemateGroupId.value &&
          studentHousemateGroup.status !== 'PENDING' &&
          studentHousemateGroup.status !== 'REJECTED'
        );
      }
    );

    const defaultGroup = studentHousemateGroups.value?.filter(
      (studentHousemateGroup) => {
        return (
          studentHousemateGroup.status !== 'PENDING' &&
          studentHousemateGroup.status !== 'REJECTED'
        );
      }
    );

    // Get the most recent group. Sorted desc by backend
    targetHousemateGroup.value =
      persistedDefault && Object.keys(persistedDefault).length > 0
        ? persistedDefault
        : defaultGroup[0];

    if (targetHousemateGroup.value && updateMenuHeader) {
      setMenuHeaderText(
        targetHousemateGroup.value?.housemate_group_name,
        currentGrouplistPropertiesCount
      );
    }
  };

  const setMenuHeaderText = (context, count = 0) => {
    showSearchSubMenu.value = true;

    if (context === 'grouplist') {
      searchSubMenuHeaderText.value = 'Grouplist';
      // Avoid showing a stale count here - active groups count will be set once its shortlists are fetched.
      searchSubMenuHeaderCount.value = 0;
      return;
    }

    if (context === 'shortlist') {
      searchSubMenuHeaderText.value = 'My shortlist';
      searchSubMenuHeaderCount.value = shortlistCount.value;
      return;
    }

    if (context === 'enquiries') {
      searchSubMenuHeaderText.value = 'Enquiries';
      searchSubMenuHeaderCount.value = Number.isFinite(count) ? count : 0;
      return;
    }

    searchSubMenuHeaderText.value = context;
    searchSubMenuHeaderCount.value = count;
  };

  const handleReturnToSearchPage = () => {
    returnToSearchPage.value = true;
    isHousemateGroupManageMode.value = false;
    showSearchSubMenu.value = false;
    searchSubMenuHeaderText.value = '';
    searchSubMenuHeaderCount.value = 0;
    showHousemateGroupDetail.value = false;
    disableFilters.value = false;
  };

  watch(showHousemateGroupDetail, (isVisible) => {
    if (!isVisible) {
      isHousemateGroupManageMode.value = false;
    }
  });

  watch(showSearchSubMenu, (isVisible) => {
    if (!isVisible) {
      isHousemateGroupManageMode.value = false;
    }
  });

  const clearHousemateGroupError = () => {
    hasGroupCreateError.value = false;
    groupCreateErrorMsg.value = null;
    disableGroupCreateButton.value = false;
  };

  const clearManageError = () => {
    hasGroupManageError.value = false;
    groupManageErrorMsg.value = null;
  };

  const setHousemateGroupManageError = (message) => {
    hasGroupManageError.value = true;
    groupManageErrorMsg.value = message ?? null;
  };

  const refreshHousemateGroupsAndTarget = async (housemateGroupId = null) => {
    await getHousemateGroups();

    if (housemateGroupId) {
      setTargetHousemateGroup(housemateGroupId);
      return;
    }

    setTargetHousemateGroup();
  };

  const resetHousemateGroupShortlists = () => {
    housemateGroupShortlists.value = [];
    housemateGroupShortlistCount.value = 0;
    housemateGroupShortlistsLoadedGroupId.value = null;
  };

  const setHousemateGroupLimit = (limit) => {
    housemateGroupCreateLimit.value = limit;
  };

  const handleAcceptHousemateGroupInvitation = (accepted) => {
    housemateGroupInviteAccepted.value = accepted;
  };

  const invalidateHousemateGroupShortlistSettings = () => {
    housemateGroupShortlistSettingsFetched.value = false;
  };

  const insertPropertyIntoList = (list, property) => {
    if (!property?.id) {
      return list;
    }

    const current = Array.isArray(list) ? list : [];
    const propertyId = String(property.id);
    const exists = current.some((item) => String(item?.id) === propertyId);
    if (exists) {
      return current;
    }

    return [...current, property];
  };

  const removePropertyFromList = (list, propertyId) => {
    const current = Array.isArray(list) ? list : [];
    if (!propertyId) {
      return current;
    }

    return current.filter((item) => String(item?.id) !== String(propertyId));
  };

  const invalidateShortlist = () => {
    shortlistFetched.value = false;
    shortlistedPropertyIdsFetched.value = false;
  };

  const isPersonallyShortlisted = (propertyId) => {
    const ids = Array.isArray(shortlistedPropertyIds.value)
      ? shortlistedPropertyIds.value
      : [];
    return ids.some((id) => idsMatch(id, propertyId));
  };

  const ensureHousemateGroupsShortlistContextLoaded = async ({
    includeSettings = true,
  } = {}) => {
    const tasks = [
      getShortlistedPropertyIds().catch(() => null),
      getHousemateGroupShortlistedPropertyIds().catch(() => null),
    ];

    if (includeSettings) {
      tasks.push(getHousemateGroupShortlistSettings().catch(() => null));
    }

    await Promise.all(tasks);
    return true;
  };

  const toggleHousemateGroupsShortlist = async ({
    propertyId,
    property = null,
    currentHousemateGroupId = null,
    isUiShortlisted = false,
  } = {}) => {
    if (!propertyId) {
      return { action: 'noop' };
    }

    await ensureHousemateGroupsShortlistContextLoaded().catch(() => null);

    const personallyShortlisted = isPersonallyShortlisted(propertyId);
    if (personallyShortlisted) {
      await removeFromShortlist(propertyId);
      return { action: 'removed-personal' };
    }

    const mappedGroupIdRaw =
      housemateGroupShortlistedByPropertyId.value?.[String(propertyId)];
    const groupIdToRemove = currentHousemateGroupId || mappedGroupIdRaw;
    const resolvedGroupIdToRemove = String(groupIdToRemove || '').trim();

    if (resolvedGroupIdToRemove) {
      try {
        await removeFromHousemateGroupShortlist({
          housemateGroupId: resolvedGroupIdToRemove,
          propertyId,
        });
        return { action: 'removed-group', groupId: resolvedGroupIdToRemove };
      } catch (_error) {
        // If mapping/default is stale or incompatible, surface chooser modal
        // rather than throwing an uncaught error from click handlers.
        return { action: 'needs-modal' };
      }
    }

    if (
      enableDefaultHousemateShortlist.value &&
      defaultShortlistDestination.value === 'personal'
    ) {
      await postAddToShortlist(propertyId, property);
      return { action: 'added-personal' };
    }

    if (
      enableDefaultHousemateShortlist.value &&
      defaultShortlistDestination.value === 'group' &&
      defaultShortlistHousemateId.value
    ) {
      const resolvedDefaultGroupId =
        toUiHousemateGroupId(defaultShortlistHousemateId.value) ||
        String(defaultShortlistHousemateId.value || '').trim();

      const permittedGroupIds = (getPermittedGroups.value || []).map((id) =>
        String(id)
      );

      if (
        !resolvedDefaultGroupId ||
        !permittedGroupIds.includes(String(resolvedDefaultGroupId))
      ) {
        return { action: 'needs-modal' };
      }

      // If UI is showing as shortlisted but we can't map the group, assume the
      // remembered default group so the user can remove deterministically.
      if (isUiShortlisted && resolvedDefaultGroupId) {
        try {
          await removeFromHousemateGroupShortlist({
            housemateGroupId: resolvedDefaultGroupId,
            propertyId,
          });
          return { action: 'removed-group', groupId: resolvedDefaultGroupId };
        } catch (_error) {
          return { action: 'needs-modal' };
        }
      }

      try {
        await addToHousemateGroupShortlist({
          housemateGroupId: resolvedDefaultGroupId,
          propertyId,
          property,
          setAsDefault: false,
        });
      } catch (_error) {
        return { action: 'needs-modal' };
      }

      return { action: 'added-group', groupId: resolvedDefaultGroupId };
    }

    return { action: 'needs-modal' };
  };

  const syncHousemateGroupShortlistsIfActive = async (housemateGroupId) => {
    // Keep grouplist count/UI in sync when possible.
    if (
      String(targetHousemateGroup.value?.housemate_group_id || '') !==
        String(housemateGroupId || '') ||
      !(showSearchSubMenu.value || showHousemateGroupDetail.value)
    ) {
      return;
    }

    await getHousemateGroupShortlists(housemateGroupId);
    searchSubMenuHeaderCount.value = Number.isFinite(
      housemateGroupShortlistCount.value
    )
      ? housemateGroupShortlistCount.value
      : 0;
  };

  const addMultipleToHousemateGroupShortlist = async ({
    housemateGroupId,
    propertyIds,
    setAsDefault = false,
  } = {}) => {
    const ids = Array.isArray(propertyIds) ? propertyIds : [];
    const normalizedIds = ids
      .map((id) => String(id || '').trim())
      .filter(Boolean);

    if (!housemateGroupId || !normalizedIds.length) {
      return null;
    }

    const resolvedGroupId = String(housemateGroupId || '').trim();
    for (let i = 0; i < normalizedIds.length; i += 1) {
      await addToHousemateGroupShortlist({
        housemateGroupId: resolvedGroupId,
        propertyId: normalizedIds[i],
        // Only need to persist default once per selection.
        setAsDefault: i === 0 ? !!setAsDefault : false,
        // Avoid reloading the grouplist on every item.
        refreshGroup: false,
      });
    }

    await syncHousemateGroupShortlistsIfActive(resolvedGroupId);

    return true;
  };

  const invalidateHousemateGroupShortlistedPropertyIds = () => {
    housemateGroupShortlistedByPropertyIdFetched.value = false;
    housemateGroupPropertyCountsByGroupId.value = {};
  };

  const filterShortlist = (filters = {}) => {
    const list = Array.isArray(shortlist.value) ? shortlist.value : [];
    return list.filter((property) => propertyMatchesFilters(property, filters));
  };

  // getters
  const nextGroupName = computed(() => {
    const housemateGroupList = studentHousemateGroups.value.filter((group) => {
      return group.is_initiator && group.student_id === studentId.value;
    });

    if (!housemateGroupList.length) {
      return 'Grouplist 1';
    }

    return `Grouplist ${housemateGroupList.length + 1}`;
  });

  const setDefaultHousemateGroupId = (id) =>
    (defaultHousemateGroupId.value = id);

  const housemateGroupShortlistedCountByGroupId = computed(() => {
    const explicitCounts = housemateGroupPropertyCountsByGroupId.value;
    if (
      explicitCounts &&
      typeof explicitCounts === 'object' &&
      Object.keys(explicitCounts).length > 0
    ) {
      const counts = {};

      for (const [groupIdRaw, countRaw] of Object.entries(explicitCounts)) {
        const groupId = toUiHousemateGroupId(groupIdRaw);
        const count = Number(countRaw);

        if (!groupId || !Number.isFinite(count)) {
          continue;
        }

        counts[groupId] = count;
      }

      return counts;
    }

    const map = housemateGroupShortlistedByPropertyId.value;
    const counts = {};

    if (!map || typeof map !== 'object') {
      return counts;
    }

    for (const groupIdRaw of Object.values(map)) {
      const groupId = toUiHousemateGroupId(groupIdRaw);
      if (!groupId) {
        continue;
      }

      counts[groupId] = (counts[groupId] || 0) + 1;
    }

    return counts;
  });

  const hasReachedCreateGroupLimit = computed(() => {
    const initiatedGroups = studentHousemateGroups.value.filter((group) => {
      return group.is_initiator && group.student_id === studentId.value;
    });

    return initiatedGroups.length >= housemateGroupCreateLimit.value;
  });

  const getPermittedGroups = computed(() => {
    return studentHousemateGroups.value
      .filter((group) => group.status === 'APPROVED')
      .map((group) => group.housemate_group_id);
  });

  const setHousemateGroupJoinError = (message) => {
    hasGroupJoinError.value = true;
    groupJoinErrorMsg.value = message;
  };

  const setStudentId = (id) => (studentId.value = id);

  const selectShortlistProperty = (id) => {
    if (!selectedPropertyIds.value.some((value) => idsMatch(value, id))) {
      selectedPropertyIds.value.push(normaliseId(id));
    }
  };

  const deselectShortlistProperty = (id) => {
    selectedPropertyIds.value = selectedPropertyIds.value.filter(
      (pid) => !idsMatch(pid, id)
    );
  };

  const toggleSelectAllShortlists = (checked) => {
    if (checked) {
      selectedPropertyIds.value = shortlist.value.map((p) =>
        normaliseId(p?.id)
      );
    } else {
      selectedPropertyIds.value = [];
    }
  };

  const syncSelectionWithShortlist = () => {
    const shortlistIds = new Set(
      shortlist.value.map((p) => normaliseId(p?.id)).filter(Boolean)
    );

    selectedPropertyIds.value = selectedPropertyIds.value.filter((id) =>
      shortlistIds.has(normaliseId(id))
    );
  };

  const clearShortlistSelection = () => {
    selectedPropertyIds.value = [];
  };

  const approvedGroupCount = computed(() => {
    return studentHousemateGroups.value.filter(
      (group) => group.status === 'APPROVED'
    ).length;
  });

  // Backward compatibility for existing consumers.
  const noAllowedGroupAccess = computed(() => approvedGroupCount.value);

  const approvedHousemateGroupsCount = computed(() => {
    return Number(approvedGroupCount.value) || 0;
  });

  const hasApprovedHousemateGroups = computed(() => {
    return approvedHousemateGroupsCount.value > 0;
  });

  const userHasOnlyPendingGroups = computed(() => {
    const pendingCount = studentHousemateGroups.value.filter(
      (group) => group.status === 'PENDING'
    ).length;

    const approvedCount = studentHousemateGroups.value.filter(
      (group) => group.status === 'APPROVED'
    ).length;

    return pendingCount > 0 && approvedCount === 0;
  });

  const getAllPendingHousemateGroups = computed(() => {
    return studentHousemateGroups.value.filter(
      (group) => group.status === 'PENDING'
    );
  });

  const shortlistedPropertiesCount = computed(() => {
    return Number.isFinite(shortlistCount.value)
      ? shortlistCount.value
      : shortlist.value.length;
  });

  const allSelected = computed(() => {
    return (
      shortlist.value.length > 0 &&
      selectedPropertyIds.value.length === shortlist.value.length
    );
  });

  const selectedCount = computed(() => selectedPropertyIds.value.length);

  const clearError = () => {
    hasGroupCreateError.value = false;
    groupCreateErrorMsg.value = null;
    disableGroupCreateButton.value = false;
  };

  const handleHousemateDataRefresh = async () => {
    if (isRefreshingManageGroup.value) return;

    isRefreshingManageGroup.value = true;
    const currentGroupId = targetHousemateGroup.value?.housemate_group_id;

    try {
      await getHousemateGroups();

      if (currentGroupId) {
        setTargetHousemateGroup(currentGroupId);

        const refreshedGroupId = targetHousemateGroup.value?.housemate_group_id;

        if (refreshedGroupId) {
          await getHousemateGroupShortlists(refreshedGroupId);
        }
      }
    } finally {
      setTargetHousemateGroup(targetHousemateGroup.value?.housemate_group_id);
      isRefreshingManageGroup.value = false;
    }
  };

  function normalizeServerErrors(err) {
    if (!err) return [];

    if (Array.isArray(err)) {
      return err;
    }

    if (typeof err === 'string') {
      return [err];
    }

    if (typeof err === 'object') {
      return Object.values(err).flat();
    }

    return [];
  }

  return {
    // state
    studentHousemateGroups,
    enquiriesProperties,
    enquiriesPropertyCount,
    showSearchSubMenu,
    showHousemateGroupDetail,
    isHousemateGroupManageMode,
    searchSubMenuHeaderText,
    searchSubMenuHeaderCount,
    returnToSearchPage,
    hasGroupCreateError,
    hasGroupJoinError,
    groupCreateErrorMsg,
    groupJoinErrorMsg,
    hasGroupManageError,
    groupManageErrorMsg,
    disableGroupCreateButton,
    disableFilters,
    housemateGroupCreateLimit,
    targetHousemateGroup,
    joinHousemateGroup,
    studentId,
    housemateGroupInviteAccepted,
    shortlist,
    sharedShortlist,
    sharedShortlistLoading,
    shortlistLoadedOnce,
    shortlistCount,
    selectedPropertyIds,
    selectedCount,
    housemateGroupShortlists,
    housemateGroupShortlistCount,
    housemateGroupShortlistsLoading,
    housemateGroupShortlistsLoadedGroupId,
    isRefreshingManageGroup,

    // getters
    nextGroupName,
    hasReachedCreateGroupLimit,
    getPermittedGroups,
    noAllowedGroupAccess,
    approvedHousemateGroupsCount,
    hasApprovedHousemateGroups,
    userHasOnlyPendingGroups,
    getAllPendingHousemateGroups,
    allSelected,
    shortlistedPropertiesCount,

    // housemate shortlist destination settings
    enableDefaultHousemateShortlist,
    defaultShortlistHousemateId,
    defaultShortlistDestination,

    housemateGroupShortlistedByPropertyId,
    housemateGroupShortlistedByPropertyIdFetched,
    housemateGroupShortlistedByPropertyIdLoading,
    housemateGroupShortlistedCountByGroupId,

    // actions
    resetHousemateGroupShortlists,
    hydrateShortlist,
    hydrateSharedShortlist,
    handleReturnToSearchPage,
    clearHousemateGroupError,
    clearManageError,
    handleAcceptHousemateGroupInvitation,
    invalidateShortlist,
    selectShortlistProperty,
    deselectShortlistProperty,
    toggleSelectAllShortlists,
    clearShortlistSelection,
    invalidateHousemateGroupShortlistSettings,
    addMultipleToHousemateGroupShortlist,
    syncSelectionWithShortlist,
    ensureHousemateGroupsShortlistContextLoaded,
    toggleHousemateGroupsShortlist,
    filterShortlist,
    filterSharedShortlist,
    isPersonallyShortlisted,
    invalidateHousemateGroupShortlistedPropertyIds,
    clearError,
    handleHousemateDataRefresh,

    // actions - API calls
    getShortlist,
    getSharedShortlist,
    refreshShortlist,
    createShortlistShareLink,
    postJoinHousemateGroup,
    getHousemateGroupShortlistedPropertyIds,
    getEnquiredProperties,
    getHousemateGroups,
    getHousemateGroupShortlists,
    voteHousemateGroupShortlistProperty,
    postHousemateGroup,
    postSetDefaultHousemateGroup,
    postAddToShortlist,
    getHousemateGroupShortlistSettings,
    postUpdateHousemateGroupShortlistSettings,
    approveHousemateGroupMember,
    declineHousemateGroupMember,
    postRenameHousemateGroup,
    deleteHousemateGroup,
    leaveHousemateGroup,
    removeHousemateGroupMember,
    removeMultipleFromShortlist,
    getShortlistedPropertyIds,
    addToHousemateGroupShortlist,
    removeFromHousemateGroupShortlist,
    removeFromShortlist,

    // setters
    setShortlistCount,
    setStudentId,
    setHousemateGroupLimit,
    setTargetHousemateGroup,
    setHousemateGroupJoinError,
    setMenuHeaderText,
    setDefaultHousemateGroupId,
    sortHousemateGroupPropertiesByVotes,
    shouldOpenHousemateGroupManage,
  };
});
